import React, { useCallback, useEffect, useState } from 'react';
import styles from './RecaptchaInterstitial.module.scss';
import Script from 'next/script';
import { gql } from '@apollo/client';
import {
	useRecaptchaInterstitial_ValidateRecaptchaMutation,
	useRecaptchaInterstitial_GetFeatureFlagsQuery,
	FeatureCategory,
	useRecaptchaInterstitial_GetGlobalVariablesQuery,
} from '#types';
import { Box, Typography } from '@mui/material';
import { Spinner } from '../Spinner';
import { useToast } from '../../hooks/useToast';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getGlobalVariablesQuery = gql`
	query RecaptchaInterstitial_getGlobalVariables {
		getGlobalVariables {
			CaptchaURL
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const validateRecaptchaMutation = gql`
	mutation RecaptchaInterstitial_ValidateRecaptcha($token: String!) {
		validateRecaptcha(token: $token)
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getFeatureFlagsQuery = gql`
	query RecaptchaInterstitial_GetFeatureFlags {
		getFeatureFlags {
			category
			active
		}
	}
`;

type Recaptcha = {
	ready: ( callback: () => void ) => void;
	execute: ( siteKey: string, options: { action: 'submit' } ) => Promise<string>;
	reset: () => void;
};

interface CustomWindow extends Window {
	grecaptcha?: {
		enterprise?: Recaptcha;
	};
}

declare const window: CustomWindow;

type RecaptchaInterstitialProps = {
	onClose: () => void;
};

export const RecaptchaInterstitial: React.FC<RecaptchaInterstitialProps> = ( {
	onClose,
} ) => {
	const [ validateRecaptcha ] =
		useRecaptchaInterstitial_ValidateRecaptchaMutation();
	const [ message, setMessage ] = useState( 'Loading...' );
	const [ recaptcha, setRecaptcha ] = useState<Recaptcha | undefined>();
	const { showError } = useToast();

	const {
		data: featureFlagData,
		error: featureFlagError,
		loading: featureFlagLoading,
	} = useRecaptchaInterstitial_GetFeatureFlagsQuery();

	const { data: globalVariablesData } =
		useRecaptchaInterstitial_GetGlobalVariablesQuery();

	useEffect( () => {
		if ( featureFlagError ) {
			showError( featureFlagError );
		}
	}, [ showError, featureFlagError ] );

	const googleRecaptchaFeatureFlagExists =
		featureFlagData?.getFeatureFlags.some(
			( { active, category } ) =>
				active && category === FeatureCategory.GoogleRecaptcha
		);

	// if the query isn't loading anymore and the GoogleRecaptcha feature flag is not truthy, just proceed as if we got a success
	useEffect( () => {
		if ( !featureFlagLoading && !googleRecaptchaFeatureFlagExists ) {
			onClose();
		}
	}, [
		featureFlagLoading,
		googleRecaptchaFeatureFlagExists,
		onClose
	] );

	const validateRecaptchaCallback = useCallback(
		async ( recaptcha: Recaptcha ) => {
			const token = await recaptcha.execute(
				'6Lc14GgsAAAAAKY9nSf8TRGxy5Ql3HU0rJJ2a55R',
				{ action: 'submit' }
			);
			const response = await validateRecaptcha( {
				variables: {
					token,
				},
			} );
			if ( response.errors ) {
				response.errors.forEach( ( error ) =>
					setMessage( error.message.replace( 'Validation Error: ', '' ) )
				);
			} else if ( response.data?.validateRecaptcha ) {
				onClose();
			} else {
				// really should never get here...
				setMessage( 'Failed to validate recaptcha' );
			}
		},
		[ onClose, validateRecaptcha ]
	);

	useEffect( () => {
		if ( recaptcha && googleRecaptchaFeatureFlagExists ) {
			validateRecaptchaCallback( recaptcha );
		}
	}, [
		validateRecaptchaCallback,
		recaptcha,
		googleRecaptchaFeatureFlagExists
	] );

	// on mount, request validation of recaptcha
	useEffect( () => {
		const interval = setInterval( () => {
			if ( window.grecaptcha?.enterprise?.ready ) {
				window.grecaptcha.enterprise.ready( () =>
					setRecaptcha( window.grecaptcha?.enterprise as Recaptcha )
				);
				clearInterval( interval );
			}
		}, 1000 );
		// on unmount, reset recaptcha
		return () => {
			// remove the badge
			document.querySelector( '.grecaptcha-badge' )?.remove();
			setRecaptcha( undefined );
		};
	}, [] );

	const captchaUrl = globalVariablesData?.getGlobalVariables.CaptchaURL;

	if ( featureFlagLoading || !googleRecaptchaFeatureFlagExists || !captchaUrl ) {
		return (
			<Box className={ styles.container }>
				<Spinner size='3rem' />
				<Typography variant='h6'>Loading</Typography>
			</Box>
		);
	}

	/* 6Lc14GgsAAAAAKY9nSf8TRGxy5Ql3HU0rJJ2a55R is the RPC App Verification sitekey, viewable here:
	https://console.cloud.google.com/security/recaptcha?project=recaptcha-1724957703073 */

	return (
		<>
			<Script src={ captchaUrl } async defer></Script>
			<Box className={ styles.container }>
				<Spinner size='3rem' />
				<Typography variant='h6'>{ message }</Typography>
			</Box>
		</>
	);
};
